import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { ArrowLeft, Plus, Save, Trash2, X } from 'lucide-react';
import { legacyApi, PurchaseOrder, PODetail } from '../../../services/legacyApi';
import { api } from '../../../services/api';
import { toast } from 'sonner';
import EntityTypeaheadPicker, { PickerOption } from '../../../components/search/EntityTypeaheadPicker';
import { RequiredLabel } from '../../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../../components/forms/formValidationUtils';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { useAuth } from '../../../context/AuthContext';

interface MaterialOption {
    material_id: number;
    name?: string;
    code?: string;
    item_type?: string;
    uom?: string;
    description?: string;
    base_unit_id?: number | null;
    base_unit_name?: string | null;
    uom_conversions?: Array<{
        from_unit_id: number;
        from_unit_name?: string | null;
        to_unit_id?: number;
        to_unit_name?: string | null;
        factor_to_base: number;
        is_base?: boolean;
    }>;
}

interface MaterialCategoryOption {
    material_category_id: number;
    material_category: string;
}

interface UnitOption {
    unit_id: number;
    unit: string;
}

interface QuickMaterialFormState {
    material_name: string;
    material_category_id: string;
    unit_id: string;
    item_type: 'Stock' | 'Consumable';
    description: string;
}

const QUICK_MATERIAL_INITIAL: QuickMaterialFormState = {
    material_name: '',
    material_category_id: '',
    unit_id: '',
    item_type: 'Stock',
    description: '',
};

const normalizeItemType = (value?: string) => {
    const normalized = String(value || 'Stock').replace(/[-_\s]/g, '').toLowerCase();
    if (['consumable', 'expense', 'nonstock'].includes(normalized)) return 'Consumable';
    if (['service', 'services'].includes(normalized)) return 'Service';
    if (['fixedasset', 'asset', 'fixedassets'].includes(normalized)) return 'FixedAsset';
    return 'Stock';
};

export const GRNForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { hasPermission } = useAuth();
    const isEdit = Boolean(id);
    const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
    const [selectedPOId, setSelectedPOId] = useState<number | ''>('');
    const [dateReceived, setDateReceived] = useState<string>(new Date().toISOString().split('T')[0]);
    const [receivedBy, setReceivedBy] = useState('');
    const [deliveryNote, setDeliveryNote] = useState('');
    const [remarks, setRemarks] = useState('');
    const [supplierName, setSupplierName] = useState('');
    const [lines, setLines] = useState<{
        po_detail_id?: number;
        material_id: number;
        material_code?: string;
        description: string;
        unit_id?: number;
        unit_name?: string;
        ordered_qty: number;
        total_received_qty: number;
        remaining_qty: number;
        quantity_received: number;
        original_quantity_received: number;
        original_base_qty?: number;
        transaction_unit_id?: number;
        is_new?: boolean;
    }[]>([]);
    const [loading, setLoading] = useState(false);
    const [generateBillAfterSave, setGenerateBillAfterSave] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [sourcePath, setSourcePath] = useState<'PO_GRN' | 'DIRECT_GRN'>('PO_GRN');
    const [materials, setMaterials] = useState<MaterialOption[]>([]);
    const [materialCategories, setMaterialCategories] = useState<MaterialCategoryOption[]>([]);
    const [units, setUnits] = useState<UnitOption[]>([]);
    const [quickMaterialOpen, setQuickMaterialOpen] = useState(false);
    const [quickMaterialSaving, setQuickMaterialSaving] = useState(false);
    const [quickMaterialTargetRow, setQuickMaterialTargetRow] = useState<number | null>(null);
    const [quickMaterialForm, setQuickMaterialForm] = useState<QuickMaterialFormState>({ ...QUICK_MATERIAL_INITIAL });
    const [quickMaterialErrors, setQuickMaterialErrors] = useState<Record<string, string>>({});
    const canQuickAddMaterial = hasPermission('inventory.materials.manage');
    const isDirectEdit = isEdit && sourcePath === 'DIRECT_GRN';

    const isPoGrnEligible = (po?: PurchaseOrder) => {
        const statusId = Number(po?.po_status_id || 0);
        if (!statusId) return true;
        return statusId !== 1 && statusId !== 7;
    };

    const poPickerOptions = useMemo<PickerOption<string>[]>(() => {
        const source = isEdit ? purchaseOrders : purchaseOrders.filter(isPoGrnEligible);
        return source.map((po) => ({
            value: String(po.po_id),
            label: po.po_number || `PO-${po.po_id}`,
            description: `${po.supplier_name || 'Unknown supplier'}${po.status_name ? ` - ${po.status_name}` : ''}`,
            badges: po.status_name ? [{ label: po.status_name, tone: isPoGrnEligible(po) ? 'green' : 'amber' }] : undefined,
        }));
    }, [isEdit, purchaseOrders]);

    const materialPickerOptions = useMemo<PickerOption<string>[]>(() => {
        return materials.map((m) => ({
            value: String(m.material_id),
            label: m.name || `Material ${m.material_id}`,
            description: [m.code, normalizeItemType(m.item_type), m.uom].filter(Boolean).join(' | ') || undefined,
            badges: [{ label: normalizeItemType(m.item_type), tone: normalizeItemType(m.item_type) === 'Consumable' ? 'amber' : 'green' }],
        }));
    }, [materials]);

    const hasNoEligiblePOs = !isEdit && poPickerOptions.length === 0;

    const normalizeMaterialRows = (matData: any[]): MaterialOption[] => {
        return Array.isArray(matData)
            ? matData.map((m) => ({
                material_id: Number(m.material_id),
                name: m.material_name,
                code: `HC/MTR/${String(m.material_id).padStart(4, '0')}`,
                item_type: normalizeItemType(m.item_type),
                uom: m.base_unit_name || m.unit || '',
                description: m.description || '',
                base_unit_id: m.base_unit_id ?? null,
                base_unit_name: m.base_unit_name || m.unit || '',
                uom_conversions: Array.isArray(m.uom_conversions) ? m.uom_conversions : [],
            }))
            : [];
    };

    const getMaterialMeta = (materialId?: number) => materials.find((m) => m.material_id === Number(materialId));

    const getUnitFactorToBase = (material: MaterialOption | undefined, unitId?: number) => {
        const resolvedUnitId = Number(unitId || material?.base_unit_id || 0);
        if (!resolvedUnitId) return 1;
        const conversion = (material?.uom_conversions || []).find((row) => Number(row.from_unit_id) === resolvedUnitId);
        return Number(conversion?.factor_to_base || 1);
    };

    const getLineConversionPreview = (line: typeof lines[number]) => {
        const material = getMaterialMeta(line.material_id);
        const qty = Number(line.quantity_received || 0);
        const txUnitId = Number(line.transaction_unit_id || line.unit_id || material?.base_unit_id || 0);
        const conversion = (material?.uom_conversions || []).find((row) => Number(row.from_unit_id) === txUnitId);
        const factor = Number(conversion?.factor_to_base || 1);
        return {
            baseQty: qty * factor,
            factor,
            baseUnitName: material?.base_unit_name || line.unit_name || material?.uom || '',
        };
    };

    const getLineReceiveMetrics = (line: typeof lines[number]) => {
        const material = getMaterialMeta(line.material_id);
        const poFactor = getUnitFactorToBase(material, line.unit_id || material?.base_unit_id || undefined);
        const receiveFactor = getUnitFactorToBase(material, line.transaction_unit_id || line.unit_id || material?.base_unit_id || undefined);
        const orderedBase = Number(line.ordered_qty || 0) * poFactor;
        const receivedBase = Number(line.total_received_qty || 0) * poFactor;
        const remainingBase = Number(line.remaining_qty || 0) * poFactor;
        const originalBase = Number(line.original_base_qty || 0);
        const divisor = receiveFactor > 0 ? receiveFactor : 1;
        return {
            ordered: orderedBase / divisor,
            received: receivedBase / divisor,
            remaining: remainingBase / divisor,
            remainingBase,
            originalBase,
        };
    };

    const formatQty = (value: number) => {
        const safe = Number(value || 0);
        if (Number.isInteger(safe)) return String(safe);
        return safe.toLocaleString(undefined, { maximumFractionDigits: 3 });
    };

    const loadMaterialOptions = async () => {
        const matData = await api.purchasing.listDirectGrnMaterialOptions();
        const normalized = normalizeMaterialRows(matData);
        setMaterials(normalized);
        return normalized;
    };

    const loadPoDetails = (poId: number) => {
        legacyApi.purchasing.getOrderDetail(Number(poId)).then(po => {
            setLines(po.details.map((d: PODetail) => ({
                po_detail_id: d.po_detail_id,
                material_id: d.material_id || 0,
                material_code: d.material_code || (d.material_id ? `HC/MTR/${String(d.material_id).padStart(4, '0')}` : undefined),
                description: d.material_name || d.description || '',
                unit_id: d.unit_id,
                unit_name: d.unit_name,
                ordered_qty: d.quantity,
                total_received_qty: d.total_qty_received || 0,
                remaining_qty: Math.max(d.quantity - (d.total_qty_received || 0), 0),
                quantity_received: Math.max(d.quantity - (d.total_qty_received || 0), 0),
                original_quantity_received: 0,
                original_base_qty: 0,
                transaction_unit_id: d.unit_id,
                is_new: false
            })));
        });
    };

    useEffect(() => {
        legacyApi.purchasing.listOrders().then(setPurchaseOrders);
        Promise.all([
            loadMaterialOptions(),
            legacyApi.masterData.list('tbl_material_categories'),
            legacyApi.masterData.listUnits(),
        ])
            .then(([, categoryRows, unitRows]) => {
                setMaterialCategories(Array.isArray(categoryRows) ? categoryRows : []);
                setUnits(Array.isArray(unitRows) ? unitRows : []);
            })
            .catch((err) => {
                console.error(err);
                toast.error('Failed to load GRN material options');
            });
    }, []);

    useEffect(() => {
        if (!selectedPOId) {
            setSupplierName('');
            return;
        }
        const selected = purchaseOrders.find(p => p.po_id === Number(selectedPOId));
        setSupplierName(selected?.supplier_name || '');
    }, [selectedPOId, purchaseOrders]);

    useEffect(() => {
        if (isEdit && id) {
            setLoading(true);
            legacyApi.purchasing.getGRNDetail(Number(id))
                .then(grn => {
                    setSelectedPOId(grn.po_id || '');
                    setDateReceived(grn.date_received ? grn.date_received.substring(0, 10) : new Date().toISOString().split('T')[0]);
                    setReceivedBy(grn.received_by || '');
                    setDeliveryNote(grn.delivery_note_no || '');
                    setRemarks(grn.remarks || '');
                    setSupplierName(grn.supplier_name || '');
                    setSourcePath(String(grn.source_path || '').toUpperCase() === 'DIRECT_GRN' ? 'DIRECT_GRN' : 'PO_GRN');
                    setLines((grn.details || []).map((d: any) => ({
                        po_detail_id: d.po_detail_id,
                        material_id: d.material_id || 0,
                        material_code: d.material_id ? `HC/MTR/${String(d.material_id).padStart(4, '0')}` : undefined,
                        description: d.material_desc || d.description || '',
                        unit_id: d.unit_id,
                        unit_name: d.unit_name,
                        ordered_qty: d.qty_ordered || 0,
                        total_received_qty: d.total_received_qty || 0,
                        remaining_qty: d.remaining_qty || 0,
                        quantity_received: d.quantity_received || 0,
                        original_quantity_received: d.quantity_received || 0,
                        original_base_qty: d.base_qty ?? d.quantity_received ?? 0,
                        transaction_unit_id: d.transaction_unit_id || d.unit_id,
                        is_new: false
                    })));
                })
                .catch((error: any) => {
                    console.error(error);
                    toast.error(error?.message || 'Failed to load GRN details');
                    navigate('/legacy/grn');
                })
                .finally(() => setLoading(false));
            return;
        }

        if (selectedPOId) {
            loadPoDetails(Number(selectedPOId));
        } else {
            setLines([]);
        }
    }, [selectedPOId, isEdit, id]);

    const addDirectLine = () => {
        setLines((prev) => [
            ...prev,
            {
                po_detail_id: undefined,
                material_id: 0,
                description: '',
                unit_id: undefined,
                unit_name: '',
                ordered_qty: 0,
                total_received_qty: 0,
                remaining_qty: 0,
                quantity_received: 1,
                original_quantity_received: 0,
                original_base_qty: 0,
                transaction_unit_id: undefined,
                is_new: true,
            },
        ]);
    };

    const removeLine = (index: number) => {
        setLines((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleLineChange = (index: number, field: string, value: any) => {
        const next = [...lines];
        const updated = { ...next[index], [field]: value };
        if (field === 'material_id') {
            const selected = materials.find((m) => m.material_id === Number(value));
            if (selected) {
                updated.description = selected.name || updated.description;
                updated.unit_name = selected.base_unit_name || selected.uom || updated.unit_name;
                updated.unit_id = selected.base_unit_id || updated.unit_id;
                updated.transaction_unit_id = selected.base_unit_id || updated.transaction_unit_id;
            }
        }
        next[index] = updated;
        setLines(next);
    };

    const openQuickMaterialModal = (targetRow?: number) => {
        if (!canQuickAddMaterial) {
            toast.error('You do not have permission to create materials.');
            return;
        }
        let rowIndex = targetRow ?? lines.length - 1;
        if (rowIndex < 0) {
            addDirectLine();
            rowIndex = 0;
        }
        setQuickMaterialTargetRow(rowIndex);
        setQuickMaterialErrors({});
        setQuickMaterialForm({ ...QUICK_MATERIAL_INITIAL });
        setQuickMaterialOpen(true);
    };

    const closeQuickMaterialModal = () => {
        if (quickMaterialSaving) return;
        setQuickMaterialOpen(false);
        setQuickMaterialTargetRow(null);
        setQuickMaterialErrors({});
        setQuickMaterialForm({ ...QUICK_MATERIAL_INITIAL });
    };

    const handleQuickMaterialSave = async () => {
        const validation = validateRequiredFields([
            { key: 'material_name', label: 'Material Name', value: quickMaterialForm.material_name, required: true },
            { key: 'material_category_id', label: 'Category', value: quickMaterialForm.material_category_id, required: true },
            { key: 'unit_id', label: 'Unit', value: quickMaterialForm.unit_id, required: true },
            { key: 'item_type', label: 'Item Type', value: quickMaterialForm.item_type, required: true },
        ]);
        if (!validation.valid) {
            setQuickMaterialErrors(validation.errorsByField);
            showValidationSummaryToast();
            return;
        }
        setQuickMaterialSaving(true);
        try {
            const created = await api.materials.create({
                material_name: quickMaterialForm.material_name.trim(),
                material_category_id: Number(quickMaterialForm.material_category_id),
                unit_id: Number(quickMaterialForm.unit_id),
                item_type: quickMaterialForm.item_type,
                description: quickMaterialForm.description.trim() || undefined,
            });
            const createdId = Number(created?.id || 0);
            const refreshed = await loadMaterialOptions();
            const selected = refreshed.find((m) => m.material_id === createdId);
            if (!selected) throw new Error('Material created but not found in refreshed options.');
            const row = quickMaterialTargetRow ?? 0;
            setLines((prev) => {
                const next = [...prev];
                if (!next[row]) {
                    next[row] = {
                        po_detail_id: undefined,
                        material_id: 0,
                        description: '',
                        unit_id: undefined,
                        unit_name: '',
                        ordered_qty: 0,
                        total_received_qty: 0,
                        remaining_qty: 0,
                        quantity_received: 1,
                        original_quantity_received: 0,
                        original_base_qty: 0,
                        transaction_unit_id: undefined,
                        is_new: true,
                    };
                }
                next[row] = {
                    ...next[row],
                    material_id: selected.material_id,
                    description: selected.name || next[row].description,
                    unit_name: selected.base_unit_name || selected.uom || next[row].unit_name,
                    unit_id: selected.base_unit_id || next[row].unit_id,
                    transaction_unit_id: selected.base_unit_id || next[row].transaction_unit_id,
                };
                return next;
            });
            toast.success(`Material ${selected.name || createdId} created and selected.`);
            closeQuickMaterialModal();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create material');
        } finally {
            setQuickMaterialSaving(false);
        }
    };

    const handleSave = async () => {
        const validation = validateRequiredFields([
            { key: 'po_id', label: 'PO #', value: selectedPOId, required: true },
            { key: 'received_by', label: 'Received By', value: receivedBy, required: true },
            { key: 'delivery_note', label: isDirectEdit ? 'Supplier DN Ref' : 'Delivery Note #', value: deliveryNote, required: true },
        ]);
        const nextErrors = { ...validation.errorsByField } as Record<string, string>;
        if (lines.length === 0) nextErrors.lines = 'Please add at least one line item';
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showValidationSummaryToast();
            focusFirstInvalidField(Object.keys(nextErrors)[0]);
            return;
        }
        setFieldErrors({});

        const detailsToReceive = lines.filter(l => Number(l.quantity_received) > 0);
        if (detailsToReceive.length === 0) {
            toast.error('Enter quantity to receive for at least one line item');
            return;
        }

        if (!isDirectEdit) {
            const invalid = detailsToReceive.some(l => {
                const preview = getLineConversionPreview(l);
                const metrics = getLineReceiveMetrics(l);
                const maxAllowedBase = metrics.remainingBase + (isEdit ? metrics.originalBase : 0);
                return preview.baseQty < 0 || preview.baseQty - maxAllowedBase > 1e-9;
            });
            if (invalid) {
                toast.error('Received quantity cannot exceed remaining quantity.');
                return;
            }
        } else {
            const missingMaterial = detailsToReceive.find((l) => !Number(l.material_id));
            if (missingMaterial) {
                toast.error('Material is required for each direct GRN line.');
                return;
            }
        }

        setLoading(true);
        try {
            if (isEdit && id) {
                await legacyApi.purchasing.updateGRN(Number(id), {
                    date_received: dateReceived,
                    received_by: receivedBy,
                    delivery_note_no: deliveryNote,
                    remarks,
                    details: detailsToReceive.map(l => ({
                        po_detail_id: l.po_detail_id || undefined,
                        quantity_received: Number(l.quantity_received),
                        material_id: Number(l.material_id),
                        description: l.description || undefined,
                        unit_id: l.unit_id || undefined,
                        transaction_unit_id: l.transaction_unit_id || l.unit_id || undefined,
                    }))
                });
                toast.success('GRN updated successfully!');
            } else {
                const created = await legacyApi.purchasing.createGRN({
                    po_id: Number(selectedPOId),
                    date_received: dateReceived,
                    received_by: receivedBy,
                    delivery_note_no: deliveryNote,
                    remarks,
                    details: detailsToReceive.map(l => ({
                        po_detail_id: l.po_detail_id,
                        quantity_received: l.quantity_received,
                        material_id: l.material_id,
                        transaction_unit_id: l.transaction_unit_id || l.unit_id || undefined,
                    }))
                });
                if (generateBillAfterSave && created?.grn_id) {
                    toast.success('GRN created. Continue to supplier bill.');
                    navigate(`/purchasing/bills/new?grn_id=${created.grn_id}`);
                    return;
                }
                toast.success('GRN created successfully!');
            }
            navigate('/legacy/grn');
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || (isEdit ? 'Failed to update GRN' : 'Failed to create GRN'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 w-full p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/legacy/grn')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold">{isEdit ? 'Edit Goods Received Note' : 'New Goods Received Note'}</h1>
                </div>
                <Button onClick={handleSave} disabled={loading}>
                    <Save className="h-4 w-4 mr-2" /> {loading ? 'Saving...' : isEdit ? 'Update GRN' : 'Save GRN'}
                </Button>
            </div>

            <FlowEntryBanner
                title="Purchasing Flow: Receive GRN"
                pathLabel="PO to Supplier Bill"
                nextHint="GRN posts stock receipt and updates PO progress. Reconcile supplier bill next."
                helpModule="purchasing"
            />

            <div className="space-y-6">
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>GRN Header Information</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">GRN ID</label>
                                <input
                                    type="text"
                                    value={isEdit && id ? `HC/GRN/${String(id).padStart(4, '0')}` : 'Auto'}
                                    readOnly
                                    className="w-full h-10 px-3 rounded-md border border-input bg-muted text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="po_id">PO #</RequiredLabel>
                                <div id="po_id" className={fieldErrors.po_id ? 'rounded-md border border-red-500 p-1' : ''}>
                                    <EntityTypeaheadPicker
                                        value={selectedPOId ? String(selectedPOId) : ''}
                                        options={poPickerOptions}
                                        placeholder={hasNoEligiblePOs ? 'No approved POs available' : 'Search PO by number or supplier...'}
                                        emptyText="No matching purchase orders"
                                        disabled={isEdit || hasNoEligiblePOs}
                                        onChange={(value) => setSelectedPOId(Number(value))}
                                    />
                                </div>
                                <FieldErrorText message={fieldErrors.po_id} />
                                {hasNoEligiblePOs && (
                                    <p className="text-xs text-amber-700">No GRN-eligible PO found. Approve a PO first, then create GRN.</p>
                                )}
                                {!isEdit && selectedPOId && (
                                    <Button type="button" variant="outline" size="sm" onClick={() => loadPoDetails(Number(selectedPOId))}>
                                        Load PO Items
                                    </Button>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Supplier</label>
                                <input
                                    type="text"
                                    value={supplierName}
                                    readOnly
                                    className="w-full h-10 px-3 rounded-md border border-input bg-muted text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="delivery_note">{isDirectEdit ? 'Supplier DN Ref' : 'Delivery Note #'}</RequiredLabel>
                                <input
                                    id="delivery_note"
                                    name="delivery_note"
                                    aria-invalid={!!fieldErrors.delivery_note}
                                    aria-required="true"
                                    type="text"
                                    value={deliveryNote}
                                    onChange={(e) => setDeliveryNote(e.target.value)}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                    placeholder={isDirectEdit ? 'DN-12345' : 'AJT-INV-12345'}
                                />
                                <FieldErrorText message={fieldErrors.delivery_note} />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Date Received</label>
                                <input
                                    type="date"
                                    value={dateReceived}
                                    onChange={(e) => setDateReceived(e.target.value)}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="received_by">Received By</RequiredLabel>
                                <input
                                    id="received_by"
                                    name="received_by"
                                    aria-invalid={!!fieldErrors.received_by}
                                    aria-required="true"
                                    type="text"
                                    value={receivedBy}
                                    onChange={(e) => setReceivedBy(e.target.value)}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                    placeholder="Employee Name"
                                />
                                <FieldErrorText message={fieldErrors.received_by} />
                            </div>
                            <div className="space-y-2 md:col-span-3">
                                <label className="text-sm font-medium">Remarks</label>
                                <input
                                    type="text"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                />
                            </div>
                            {!isEdit && (
                                <div className="md:col-span-3 flex items-center gap-2 rounded border bg-muted/20 px-3 py-2">
                                    <input
                                        id="generate_bill_after_save"
                                        type="checkbox"
                                        checked={generateBillAfterSave}
                                        onChange={(e) => setGenerateBillAfterSave(e.target.checked)}
                                    />
                                    <label htmlFor="generate_bill_after_save" className="text-sm">
                                        After saving GRN, continue to create Supplier Bill
                                    </label>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Received Items</CardTitle>
                                {isDirectEdit && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openQuickMaterialModal()}
                                        disabled={!canQuickAddMaterial}
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add New Material
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {fieldErrors.lines && (
                                <div className="mb-2">
                                    <FieldErrorText message={fieldErrors.lines} />
                                </div>
                            )}
                            {!selectedPOId ? (
                                <div className="text-center py-8 text-muted-foreground">Select a PO to load items</div>
                            ) : isDirectEdit ? (
                                <>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b text-left text-muted-foreground">
                                                <th className="pb-3 font-medium">Code</th>
                                                <th className="pb-3 font-medium">Material</th>
                                                <th className="pb-3 font-medium">Base UOM</th>
                                                <th className="pb-3 font-medium">Receive UOM</th>
                                                <th className="pb-3 font-medium text-right">Receive Now</th>
                                                <th className="pb-3 font-medium text-right">Base Qty</th>
                                                <th className="pb-3 font-medium text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lines.map((line, idx) => {
                                                const material = getMaterialMeta(line.material_id);
                                                const preview = getLineConversionPreview(line);
                                                return (
                                                <tr key={idx} className="border-b last:border-0">
                                                    <td className="py-2 font-mono">{line.material_code || '-'}</td>
                                                    <td className="py-2">
                                                        {line.is_new || !line.po_detail_id ? (
                                                            <div className="space-y-1">
                                                                <EntityTypeaheadPicker
                                                                    value={line.material_id ? String(line.material_id) : ''}
                                                                    options={materialPickerOptions}
                                                                    placeholder="Search material..."
                                                                    emptyText="No matching materials"
                                                                    loading={loading}
                                                                    onChange={(value) => handleLineChange(idx, 'material_id', Number(value))}
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 px-2 text-xs text-blue-600"
                                                                    onClick={() => openQuickMaterialModal(idx)}
                                                                    disabled={!canQuickAddMaterial}
                                                                >
                                                                    + New Material
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <span>{line.description}</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2">{material?.base_unit_name || line.unit_name || '-'}</td>
                                                    <td className="py-2">
                                                        <select
                                                            value={line.transaction_unit_id || material?.base_unit_id || ''}
                                                            onChange={(e) => handleLineChange(idx, 'transaction_unit_id', Number(e.target.value))}
                                                            className="w-32 h-8 px-2 rounded-md border border-input bg-background text-sm"
                                                        >
                                                            {(material?.uom_conversions || []).map((option) => (
                                                                <option key={`${line.material_id}-${option.from_unit_id}`} value={option.from_unit_id}>
                                                                    {option.from_unit_name || option.to_unit_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="py-2 text-right">
                                                        <input
                                                            type="number"
                                                            value={line.quantity_received}
                                                            onChange={(e) => handleLineChange(idx, 'quantity_received', Number(e.target.value))}
                                                            className="w-24 h-8 px-2 rounded-md border border-input bg-background text-sm text-right ml-auto"
                                                            min="0"
                                                            step="0.001"
                                                        />
                                                    </td>
                                                    <td className="py-2 text-right">{preview.baseQty.toFixed(3)} {preview.baseUnitName}</td>
                                                    <td className="py-2 text-right">
                                                        <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                                                            <Trash2 className="h-4 w-4 text-red-600" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            )})}
                                        </tbody>
                                    </table>
                                    <div className="mt-4 flex justify-start">
                                        <Button type="button" variant="outline" size="sm" onClick={addDirectLine}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add Item
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b text-left text-muted-foreground">
                                                <th className="pb-3 font-medium">Code</th>
                                                <th className="pb-3 font-medium">Material</th>
                                                <th className="pb-3 font-medium">Base UOM</th>
                                                <th className="pb-3 font-medium text-right">Ordered</th>
                                                <th className="pb-3 font-medium text-right">Received</th>
                                                <th className="pb-3 font-medium text-right">Remaining</th>
                                                <th className="pb-3 font-medium">Receive UOM</th>
                                                <th className="pb-3 font-medium text-right">Receive Now</th>
                                                <th className="pb-3 font-medium text-right">Base Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lines.map((line, idx) => {
                                                const material = getMaterialMeta(line.material_id);
                                                const preview = getLineConversionPreview(line);
                                                const metrics = getLineReceiveMetrics(line);
                                                return (
                                                <tr key={idx} className="border-b last:border-0 h-10">
                                                    <td className="py-2 font-mono">{line.material_code || '-'}</td>
                                                    <td className="py-2">{line.description}</td>
                                                    <td className="py-2">{material?.base_unit_name || line.unit_name || '-'}</td>
                                                    <td className="py-2 text-right">{formatQty(metrics.ordered)}</td>
                                                    <td className="py-2 text-right">{formatQty(metrics.received)}</td>
                                                    <td className="py-2 text-right">{formatQty(metrics.remaining)}</td>
                                                    <td className="py-2">
                                                        <select
                                                            value={line.transaction_unit_id || line.unit_id || material?.base_unit_id || ''}
                                                            onChange={(e) => handleLineChange(idx, 'transaction_unit_id', Number(e.target.value))}
                                                            className="w-28 h-8 px-2 rounded-md border border-input bg-background text-sm"
                                                        >
                                                            {(material?.uom_conversions || []).map((option) => (
                                                                <option key={`${line.material_id}-${option.from_unit_id}`} value={option.from_unit_id}>
                                                                    {option.from_unit_name || option.to_unit_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="py-2 text-right">
                                                        <input
                                                            type="number"
                                                            value={line.quantity_received}
                                                            onChange={(e) => handleLineChange(idx, 'quantity_received', Number(e.target.value))}
                                                            className="w-24 h-8 px-2 rounded-md border border-input bg-background text-sm text-right ml-auto"
                                                            min="0"
                                                            step="0.001"
                                                        />
                                                    </td>
                                                    <td className="py-2 text-right">{preview.baseQty.toFixed(3)} {preview.baseUnitName}</td>
                                                </tr>
                                            )})}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
                <WorkflowRightSidebar mode="overlay" title="GRN Workflow & Status">
                    <WorkflowRightPanel
                        title="Workflow & Status"
                        description="PO lifecycle guidance and next step"
                    >
                        {selectedPOId ? (
                            <FlowCoachPanel entity="PO" entityId={Number(selectedPOId)} />
                        ) : (
                            <div className="rounded border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                                Select a PO to load workflow guidance.
                            </div>
                        )}
                    </WorkflowRightPanel>
                </WorkflowRightSidebar>
            </div>
            {quickMaterialOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-xl rounded-lg border bg-white p-5 shadow-xl">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Quick Add Material</h3>
                            <Button type="button" variant="ghost" size="icon" onClick={closeQuickMaterialModal} disabled={quickMaterialSaving}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2 md:col-span-2">
                                <RequiredLabel required htmlFor="quick_material_name">Material Name</RequiredLabel>
                                <input
                                    id="quick_material_name"
                                    value={quickMaterialForm.material_name}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, material_name: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                                <FieldErrorText message={quickMaterialErrors.material_name} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="quick_material_category">Category</RequiredLabel>
                                <select
                                    id="quick_material_category"
                                    value={quickMaterialForm.material_category_id}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, material_category_id: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select category...</option>
                                    {materialCategories.map((cat) => (
                                        <option key={cat.material_category_id} value={cat.material_category_id}>
                                            {cat.material_category}
                                        </option>
                                    ))}
                                </select>
                                <FieldErrorText message={quickMaterialErrors.material_category_id} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="quick_material_unit">Unit</RequiredLabel>
                                <select
                                    id="quick_material_unit"
                                    value={quickMaterialForm.unit_id}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, unit_id: e.target.value }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Select unit...</option>
                                    {units.map((u) => (
                                        <option key={u.unit_id} value={u.unit_id}>
                                            {u.unit}
                                        </option>
                                    ))}
                                </select>
                                <FieldErrorText message={quickMaterialErrors.unit_id} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="quick_material_item_type">Item Type</RequiredLabel>
                                <select
                                    id="quick_material_item_type"
                                    value={quickMaterialForm.item_type}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, item_type: e.target.value as 'Stock' | 'Consumable' }))}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="Stock">Stock / Storable</option>
                                    <option value="Consumable">Consumable / Expense</option>
                                </select>
                                <p className="text-xs text-muted-foreground">GRN quick-add supports Stock and Consumable materials. Consumables are not stock-valued.</p>
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <label className="text-sm font-medium" htmlFor="quick_material_description">Description</label>
                                <textarea
                                    id="quick_material_description"
                                    rows={3}
                                    value={quickMaterialForm.description}
                                    onChange={(e) => setQuickMaterialForm((prev) => ({ ...prev, description: e.target.value }))}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                            </div>
                        </div>
                        <div className="mt-5 flex items-center justify-end gap-2">
                            <Button type="button" variant="outline" onClick={closeQuickMaterialModal} disabled={quickMaterialSaving}>
                                Cancel
                            </Button>
                            <Button type="button" onClick={handleQuickMaterialSave} disabled={quickMaterialSaving}>
                                {quickMaterialSaving ? 'Saving...' : 'Create Material'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
